import { TokenIconRow } from "@/components/brand/TokenIcon";
import { PageHero, SoftLink } from "@/components/ui/PageHero";
import { VAULT_COLLATERAL_ASSETS } from "@/lib/token-assets";
import { MOTOKO_DOLLAR_LOGO, ROUTES } from "@/lib/routes";
import { Link } from "react-router-dom";
import { AnimatedSection } from "@/components/layout/AnimatedSection";
import { useCertifiedAnalytics } from "@/hooks/useCertifiedAnalytics";
import { useGetPublicAnalyticsSummary } from "@/hooks/usePublicProtocol";
import { formatNum } from "@/lib/utils";

const FLOW = [
	"Deposit ICP, ckBTC, or ckETH into a vault",
	"Mint Motoko Dollar ($USDM) up to the max LTV",
	"Repay USDM to reduce debt",
	"Withdraw excess collateral when healthy",
];

export default function MotokoDollarPage() {
	const { data: certified } = useCertifiedAnalytics();
	const { data: summary } = useGetPublicAnalyticsSummary();
	const tvl = certified?.tvlUsd ?? summary?.tvl;
	const debt = certified?.totalDebtUsd ?? summary?.totalDebt;
	const source = certified?.source ?? summary?.source ?? "offline";

	return (
		<div className="pb-16">
			<PageHero
				eyebrow="Motoko Dollar · $USDM"
				title="Overcollateralized USD on ICP"
				subtitle="Target peg ~$1 via oracle-priced collateral, risk parameters, and liquidations — not rebasing seigniorage."
				actions={
					<>
						<Link
							to={ROUTES.borrow}
							className="inline-flex h-10 items-center rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground"
						>
							Mint USDM
						</Link>
						<SoftLink to={ROUTES.vaults}>Explore vaults</SoftLink>
						<img
							src={MOTOKO_DOLLAR_LOGO}
							alt=""
							className="ml-auto hidden size-14 object-contain sm:block"
						/>
					</>
				}
			/>
			<div className="container mx-auto grid gap-6 px-4 lg:grid-cols-2">
				<AnimatedSection className="glass-panel p-5">
					<p className="section-eyebrow">Target peg · protocol stats ({source})</p>
					<p className="mt-2 font-mono-amount text-3xl font-bold">$1.0000</p>
					<p className="mt-1 text-xs text-muted-foreground">
						Design target — not a live secondary-market quote.
					</p>
					{tvl != null ? (
						<p className="mt-3 text-sm text-muted-foreground">
							TVL ${formatNum(tvl, 0)}
							{debt != null ? ` · debt $${formatNum(debt, 0)}` : ""}
						</p>
					) : (
						<p className="mt-3 text-sm text-muted-foreground">
							Connect protocol env for live TVL / debt.
						</p>
					)}
					<p className="mt-3 text-sm text-muted-foreground">
						Vault collateral: ICP · ckBTC · ckETH only.
					</p>
					<TokenIconRow
						tokens={VAULT_COLLATERAL_ASSETS.map((a) => a.id)}
						size="md"
						className="mt-4 justify-start"
					/>
				</AnimatedSection>
				<div id="flow">
				<AnimatedSection className="glass-panel p-5">
					<p className="section-eyebrow mb-4">How it works</p>
					<ol className="relative space-y-0 border-l border-primary/30 pl-6">
						{FLOW.map((step, i) => (
							<li key={step} className="relative pb-6 last:pb-0">
								<span className="absolute -left-[1.6rem] top-0 flex size-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
									{i + 1}
								</span>
								<p className="text-sm">{step}</p>
							</li>
						))}
					</ol>
				</AnimatedSection>
				</div>
			</div>
		</div>
	);
}
